import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase-config';

const TRACKABLE_PATH_MODES = new Set(['public', 'plans', 'trial', 'commercial-request']);
const SESSION_ID_KEY = 'gcentra_public_traffic_session_id';
const SESSION_STARTED_KEY = 'gcentra_public_traffic_session_started';
const SESSION_PAGES_KEY = 'gcentra_public_traffic_pages_seen';

const buildSessionId = () => `visit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const safeJsonParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const getSessionId = () => {
  if (typeof window === 'undefined') return '';
  const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
  if (existing) return existing;
  const next = buildSessionId();
  window.sessionStorage.setItem(SESSION_ID_KEY, next);
  return next;
};

const getSeenPages = () => {
  if (typeof window === 'undefined') return new Set();
  const raw = window.sessionStorage.getItem(SESSION_PAGES_KEY);
  const parsed = Array.isArray(safeJsonParse(raw, [])) ? safeJsonParse(raw, []) : [];
  return new Set(parsed.map((item) => String(item || '').trim()).filter(Boolean));
};

const persistSeenPages = (pagesSet) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(SESSION_PAGES_KEY, JSON.stringify(Array.from(pagesSet)));
};

const detectDeviceType = () => {
  if (typeof window === 'undefined') return 'unknown';
  const width = Number(window.innerWidth || 0);
  if (width && width < 768) return 'mobile';
  if (width && width < 1100) return 'tablet';
  return 'desktop';
};

export const shouldTrackPublicTraffic = (pathMode = '') => TRACKABLE_PATH_MODES.has(String(pathMode || '').trim());

export const resolvePublicTrafficPageMeta = (pathname = '/', pathMode = 'public') => {
  const path = String(pathname || '/').trim() || '/';

  if (pathMode === 'plans') {
    return { key: 'landing_plans', label: 'Planes', interest: 'plans' };
  }

  if (pathMode === 'trial') {
    return { key: 'trial_signup', label: 'Probar gratis', interest: 'trial' };
  }

  if (pathMode === 'commercial-request') {
    return { key: 'commercial_request', label: 'Solicitud comercial', interest: 'commercial_request' };
  }

  if (path === '/' || pathMode === 'public') {
    return { key: 'landing_home', label: 'Inicio', interest: 'landing' };
  }

  return { key: 'landing_other', label: 'Otra página pública', interest: 'other' };
};

const readUtmParams = () => {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search || '');
  return {
    utmSource: String(params.get('utm_source') || '').trim(),
    utmMedium: String(params.get('utm_medium') || '').trim(),
    utmCampaign: String(params.get('utm_campaign') || '').trim(),
  };
};

export const trackPublicPageVisit = async ({ pathname = '/', pathMode = 'public' } = {}) => {
  if (typeof window === 'undefined' || !shouldTrackPublicTraffic(pathMode)) {
    return { tracked: false, reason: 'not-trackable' };
  }

  const pageMeta = resolvePublicTrafficPageMeta(pathname, pathMode);
  const sessionId = getSessionId();
  if (!sessionId) {
    return { tracked: false, reason: 'missing-session' };
  }

  const seenPages = getSeenPages();
  if (seenPages.has(pageMeta.key)) {
    return { tracked: false, reason: 'already-tracked-in-session' };
  }

  const isSessionStart = window.sessionStorage.getItem(SESSION_STARTED_KEY) !== '1';
  const { utmSource, utmMedium, utmCampaign } = readUtmParams();

  const logVisit = httpsCallable(functions, 'logPublicVisitNow');

  await logVisit({
    sessionId,
    pageKey: pageMeta.key,
    pageLabel: pageMeta.label,
    pathMode,
    pathname,
    interestKey: pageMeta.interest,
    isSessionStart,
    referrer: String(document.referrer || '').trim(),
    utmSource,
    utmMedium,
    utmCampaign,
    deviceType: detectDeviceType(),
    screenWidth: Number(window.innerWidth || 0),
  });

  seenPages.add(pageMeta.key);
  persistSeenPages(seenPages);
  if (isSessionStart) {
    window.sessionStorage.setItem(SESSION_STARTED_KEY, '1');
  }

  return { tracked: true, pageKey: pageMeta.key };
};
